import {useLocalStorageState} from '@coolcolduk/ui-common-hook';
import {ContentContainer} from '@coolcolduk/ui-mui';
import {ExpandMore, Settings} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  FormControl,
  FormLabel,
  Slider,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import {Link as RouterLink, useLocation, useNavigate} from 'react-router';
import {ExamModule, QuizTimedMode} from '@cp/model';
import DropdownModule from '../../atoms/dropdownModule/DropdownModule';
import PageHeader from '../../components/PageHeader';
import TopicSelection from '../../components/TopicSelection/TopicSelection';
import {env} from '../../config/env';
import {PrivateRouteProps} from '../../config/routeType';
import useListUserActiveSession from '../../hooks/quizSession/useListUserActiveSession';
import {useCreateAndNavigateToQuizSession} from '../../hooks/quizSession/useCreateAndNavigateToQuizSession';
import useListExamModules from '../../hooks/examModules/useListExamModules';
import SessionResumeDialog from './SessionResumeDialog';
import StudySessionModuleError from './StudySessionModuleError';

export default function NewSessionPage({user}: PrivateRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedModuleId, setSelectedModuleId] = useState<string>('');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [sessionMode, setSessionMode] = useState<QuizTimedMode>(QuizTimedMode.PRACTICE);
  const [timePerQuestion, setTimePerQuestion] = useLocalStorageState(`timePerQuestion_${user.id}`, 120);
  const [totalTime, setTotalTime] = useLocalStorageState(`totalTime_${user.id}`, 600);
  const [error, setError] = useState<string | null>(null);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [topicsAccordionExpanded, setTopicsAccordionExpanded] = useState(false);

  const {examModules, loading: modulesLoading, error: modulesError} = useListExamModules();
  const {createAndNavigate, loading} = useCreateAndNavigateToQuizSession();
  const {hasActiveSession, mostRecentSession} = useListUserActiveSession(selectedModuleId);

  // Get user's selected modules
  const userSelectedModules = useMemo(() => user.selectedModules || [], [user.selectedModules]);
  const hasUserModules = userSelectedModules.length > 0;

  // Get full module details for user's selected modules
  const userModules = useMemo(
    () =>
      userSelectedModules
        .map((userModule) => examModules.find((module) => module.id === userModule.moduleId))
        .filter(Boolean),
    [userSelectedModules, examModules],
  );

  // Initialize with user's selected modules or module from navigation state
  useEffect(() => {
    const stateModuleId = (location.state as {moduleId?: string})?.moduleId;

    if (stateModuleId) {
      // If module ID is passed from navigation state, use it
      setSelectedModuleId(stateModuleId);
      const userModule = user.selectedModules?.find((um) => um.moduleId === stateModuleId);
      setSelectedTopics(userModule?.topics || []);
    } else if (user.selectedModules && user.selectedModules.length > 0) {
      const firstModule = user.selectedModules[0];
      setSelectedModuleId(firstModule.moduleId || '');
      setSelectedTopics(firstModule.topics || []);
    } else {
      setSelectedModuleId('');
      setSelectedTopics([]);
    }
  }, [user.selectedModules, location.state]);

  // Auto-select when there's only one module
  useEffect(() => {
    if (userModules.length === 1 && userModules[0] && !selectedModuleId) {
      const singleModule = userModules[0];
      setSelectedModuleId(singleModule.id);
      // Find the user's selected topics for this module
      const userModule = userSelectedModules.find((um) => um.moduleId === singleModule.id);
      setSelectedTopics(userModule?.topics || []);
    }
  }, [userModules, selectedModuleId, userSelectedModules]);

  const handleModuleChange = (moduleId: string) => {
    setSelectedModuleId(moduleId);
    // When changing module, select all topics from the new module
    const newModule = examModules.find((m) => m.id === moduleId);
    if (newModule) {
      setSelectedTopics(newModule.topics.map((t) => t.topic));
    } else {
      setSelectedTopics([]);
    }
  };

  const handleTopicSelectionChange = (newSelection: string[]) => {
    setSelectedTopics(newSelection);
  };

  const createNewSession = async () => {
    setError(null);

    try {
      // Get the selected module
      const selectedModule = examModules.find((m) => m.id === selectedModuleId);
      if (!selectedModule) {
        setError('Please select a module');
        return;
      }

      // Create session with mode and timing configuration
      let timeConfig;
      if (sessionMode === QuizTimedMode.TIMED) {
        timeConfig = totalTime;
      } else if (sessionMode === QuizTimedMode.TIMED_PER_QUESTION) {
        timeConfig = timePerQuestion;
      }

      // If selectedTopics is empty, pass undefined to use all topics from the module
      const topicsToUse = selectedTopics.length === 0 ? undefined : selectedTopics;

      // Create session and navigate (hook handles navigation)
      await createAndNavigate(selectedModule, topicsToUse, {
        difficulty: undefined, // auto only for now
        count: questionCount,
        timedMode: sessionMode,
        timeDurationSeconds: timeConfig,
        navigationType: 'session',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to start study session');
    }
  };

  const handleStartSession = async () => {
    if (!hasUserModules) {
      setError('Please add a module first to start studying');
      return;
    }

    if (!selectedModuleId) {
      setError('Please select a module');
      return;
    }

    // Check if there's an active session for this module
    if (hasActiveSession && mostRecentSession) {
      // Blur so no element behind the dialog (e.g. topic tree checkbox) retains focus when
      // the dialog applies aria-hidden to the backdrop, avoiding a11y violation.
      (document.activeElement as HTMLElement)?.blur();
      setShowResumeDialog(true);
      return;
    }

    await createNewSession();
  };

  const handleResumeSession = (sessionId: string) => {
    setShowResumeDialog(false);
    navigate(`/session/${sessionId}`);
  };

  const handleStartNewSession = async () => {
    setShowResumeDialog(false);
    await createNewSession();
  };

  if (modulesLoading) {
    return (
      <ContentContainer data-testid="new-session-page-loading">
        <Box sx={{textAlign: 'center', py: 4}}>
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{mt: 2}}>
            Loading available subjects...
          </Typography>
        </Box>
      </ContentContainer>
    );
  }

  if (modulesError) {
    return <StudySessionModuleError open onClose={() => navigate('/')} />;
  }

  const selectedModule = examModules.find((m) => m.id === selectedModuleId);

  return (
    <ContentContainer data-testid="new-session-page">
      <PageHeader title="Start New Study Session" />

      <Stack spacing={3} sx={{mt: 1}}>
        {/* Module Selection */}
        <FormControl fullWidth>
          <FormLabel>Subject &amp; Exam Board</FormLabel>
          {hasUserModules ? (
            <Box sx={{mt: 1}}>
              <DropdownModule
                moduleId={selectedModuleId}
                onChange={handleModuleChange}
                examModules={userModules.filter((m) => m != null) as ExamModule[]}
                label=""
                autoSelectFirst={true}
              />
            </Box>
          ) : (
            <Box
              sx={{mt: 1, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center'}}
              data-testid="no-modules-message"
            >
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                No modules selected. Add a module to start studying.
              </Typography>
              <Button
                component={RouterLink}
                to="/settings/module"
                variant="outlined"
                startIcon={<Settings />}
                size="small"
                data-testid="add-module-button"
              >
                Add Module
              </Button>
            </Box>
          )}

          {/* Add Module Button for users with existing modules */}
          {hasUserModules && (
            <Box sx={{mt: 1, textAlign: 'right'}}>
              <Button component={RouterLink} to="/settings/module" variant="text" startIcon={<Settings />} size="small">
                Manage Modules
              </Button>
            </Box>
          )}
        </FormControl>

        {/* Topic Selection */}
        {selectedModule && (
          <Accordion
            expanded={topicsAccordionExpanded}
            onChange={(_, isExpanded) => setTopicsAccordionExpanded(isExpanded)}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Topics to Study</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {topicsAccordionExpanded && (
                <TopicSelection
                  moduleTopics={selectedModule.topics}
                  selectedTopics={selectedTopics}
                  onSelectionChange={handleTopicSelectionChange}
                  height={300}
                  showSelectAll
                />
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Session Mode Selection */}
        <FormControl fullWidth>
          <FormLabel>Session Mode</FormLabel>
          <ButtonGroup fullWidth variant="outlined" orientation={isMobile ? 'vertical' : 'horizontal'} sx={{mt: 1}}>
            <Button
              variant={sessionMode === QuizTimedMode.PRACTICE ? 'contained' : 'outlined'}
              onClick={() => setSessionMode(QuizTimedMode.PRACTICE)}
              sx={{textAlign: 'left', justifyContent: 'flex-start'}}
            >
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>
                  Practice Mode
                </Typography>
                <Typography variant="caption" sx={{display: 'block'}}>
                  No time limits, free navigation
                </Typography>
              </Box>
            </Button>
            <Button
              variant={sessionMode === QuizTimedMode.TIMED ? 'contained' : 'outlined'}
              onClick={() => setSessionMode(QuizTimedMode.TIMED)}
              sx={{textAlign: 'left', justifyContent: 'flex-start'}}
            >
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>
                  Timed Mode
                </Typography>
                <Typography variant="caption" sx={{display: 'block'}}>
                  Overall time limit, free navigation
                </Typography>
              </Box>
            </Button>
            <Button
              variant={sessionMode === QuizTimedMode.TIMED_PER_QUESTION ? 'contained' : 'outlined'}
              onClick={() => setSessionMode(QuizTimedMode.TIMED_PER_QUESTION)}
              sx={{textAlign: 'left', justifyContent: 'flex-start'}}
            >
              <Box>
                <Typography variant="body2" sx={{fontWeight: 600}}>
                  Timed per Question
                </Typography>
                <Typography variant="caption" sx={{display: 'block'}}>
                  Fixed time per question, no navigation
                </Typography>
              </Box>
            </Button>
          </ButtonGroup>
        </FormControl>

        {/* Number of Questions */}
        <FormControl fullWidth>
          <FormLabel>Number of Questions</FormLabel>
          <Box sx={{px: 2, py: 1}}>
            <Typography variant="body2" sx={{mb: 1}}>
              Questions: {questionCount}
            </Typography>
            <Slider
              value={questionCount}
              onChange={(_, value) => setQuestionCount(value as number)}
              min={5}
              max={50}
              step={5}
              marks={[
                {value: 5, label: '5'},
                {value: 10, label: '10'},
                {value: 15, label: '15'},
                {value: 20, label: '20'},
                {value: 25, label: '25'},
                {value: 30, label: '30'},
                {value: 35, label: '35'},
                {value: 40, label: '40'},
                {value: 45, label: '45'},
                {value: 50, label: '50'},
              ]}
              valueLabelDisplay="auto"
            />
            {env.isDev && (
              <TextField
                label="Or type a number"
                type="number"
                value={questionCount}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value >= 1 && value <= 50) {
                    setQuestionCount(value);
                  }
                }}
                inputProps={{min: 1, max: 50}}
                size="small"
                sx={{mt: 2, maxWidth: 120}}
              />
            )}
          </Box>
        </FormControl>

        {/* Timing Configuration */}
        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
          {sessionMode === QuizTimedMode.TIMED && (
            <TextField
              label="Total Time (minutes)"
              type="number"
              value={Math.round(totalTime / 60)}
              onChange={(e) => setTotalTime(Number(e.target.value) * 60)}
              inputProps={{min: 5, max: 120}}
              fullWidth
            />
          )}

          {sessionMode === QuizTimedMode.TIMED_PER_QUESTION && (
            <TextField
              label="Time per Question (seconds)"
              type="number"
              value={timePerQuestion}
              onChange={(e) => setTimePerQuestion(Number(e.target.value))}
              inputProps={{min: 30, max: 300}}
              fullWidth
            />
          )}
        </Box>

        {/* Error Display */}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {/* Action Buttons */}
        <Box sx={{display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3}}>
          <Button onClick={() => navigate('/')} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleStartSession}
            variant="contained"
            disabled={loading || !hasUserModules || !selectedModuleId}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            data-testid="start-session-button"
          >
            {loading ? 'Starting...' : 'Start Session'}
          </Button>
        </Box>
      </Stack>

      {/* Session Resume Dialog */}
      {mostRecentSession && (
        <SessionResumeDialog
          open={showResumeDialog}
          onClose={() => setShowResumeDialog(false)}
          activeSession={mostRecentSession}
          onResume={handleResumeSession}
          onStartNew={handleStartNewSession}
        />
      )}
    </ContentContainer>
  );
}
